/**
 * Acquisition time for a sensor that only tells us how many samples it sent.
 *
 * The Muse device's own tick counter is useless as a sample clock on firmware 3.1.19
 * (~268 ms per tick), so each stream is timed by counting samples from an
 * arrival-time anchor at the nominal rate. Lost samples (from the packet and
 * per-sensor counters) advance the count. The anchor follows browser arrival
 * very slowly, so a headset whose crystal runs a little fast or slow does not
 * drift away from wall time; a gross disagreement re-anchors and reports a
 * break, which the pipeline treats as a genuine discontinuity.
 */

export type ClockStamp = { t0: number; gapBefore: number };

export const CLOCK_BREAK = Number.POSITIVE_INFINITY;

export class SensorClock {
  #anchor: number | null = null;
  #count = 0;
  readonly #slew: number;
  readonly #maxErrorSeconds: number;

  /**
   * @param rate nominal samples per second
   * @param options.slew fraction of the arrival error absorbed per block
   * @param options.maxErrorSeconds disagreement with arrival that forces a re-anchor
   */
  constructor(readonly rate: number, options: { slew?: number; maxErrorSeconds?: number } = {}) {
    this.#slew = options.slew ?? 0.002;
    this.#maxErrorSeconds = options.maxErrorSeconds ?? 1;
  }

  reset(): void {
    this.#anchor = null;
    this.#count = 0;
  }

  /**
   * @param samples samples in this block
   * @param arrivalMs browser time the block arrived (its last sample's latest possible time)
   * @param lostSamples samples known to be missing immediately before this block
   */
  stamp(samples: number, arrivalMs: number, lostSamples = 0): ClockStamp {
    const arrivalEnd = arrivalMs / 1000;
    if (this.#anchor === null) {
      this.#anchor = arrivalEnd - samples / this.rate;
      this.#count = samples;
      return { t0: this.#anchor, gapBefore: 0 };
    }
    const lost = Math.max(0, Math.round(lostSamples));
    const predictedEnd = this.#anchor + (this.#count + lost + samples) / this.rate;
    const error = arrivalEnd - predictedEnd;
    if (Math.abs(error) > this.#maxErrorSeconds) {
      // Never step backwards in acquisition time: a burst arriving late is
      // re-anchored after the last stamped sample, never before it.
      const lastEnd = this.#anchor + this.#count / this.rate;
      this.#anchor = Math.max(arrivalEnd - samples / this.rate, lastEnd);
      this.#count = samples;
      return { t0: this.#anchor, gapBefore: CLOCK_BREAK };
    }
    this.#anchor += error * this.#slew;
    const t0 = this.#anchor + (this.#count + lost) / this.rate;
    this.#count += lost + samples;
    return { t0, gapBefore: lost };
  }
}

/**
 * Lost subpackets between two per-sensor u8 sequence numbers, counted only when
 * the packet counter also shows a lost notification. Firmware 3.1.19 sometimes
 * steps a sensor sequence by one more than it should, and a phantom gap would
 * insert samples that never existed.
 */
export function lostSubpackets(previousSequence: number | null, sequence: number, packetsLost: boolean): number {
  if (previousSequence === null || !packetsLost) return 0;
  const step = (sequence - previousSequence + 256) % 256;
  return step > 1 ? step - 1 : 0;
}
